/**
 * PaymentSheet — the single Mini App payment component. Always offers BOTH
 * Telegram Stars and a coupon. `onApplyCoupon` is required, so a payment site
 * cannot ship Stars-only. Presentational: it does not know prices or call
 * useSkuInvoice — the caller wires onPay / onApplyCoupon to purchase / redeemCoupon.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { InvoiceSheetShell } from './InvoiceSheetShell';
import { SkuInvoiceError } from '../../hooks/useSkuInvoice';
import styles from './InvoiceSheet.module.css';

export interface PaymentSheetProps {
  skuCode: string;
  title: string;
  subtitle?: string;
  priceStars: number;
  bullets: string[];
  balanceHtml?: string;
  thumbUrl?: string;
  glyph?: ReactNode;
  background?: ReactNode;
  /** Override the Pay label (default: "Pay {stars} ⭐"). */
  ctaLabel?: string;
  /** Set true while the Stars path is in flight (caller-controlled). */
  isPaying?: boolean;
  onPay: () => void;
  onApplyCoupon: (code: string) => Promise<void>;
  onCancel: () => void;
  testId?: string;
  payTestId?: string;
  cancelTestId?: string;
}

/** Map a coupon failure to distinct copy (key + defaultValue). */
function couponError(err: unknown): { key: string; def: string } {
  if (err instanceof SkuInvoiceError) {
    const m = err.message;
    if (m.includes('discount_not_supported_yet'))
      return { key: 'avatarFlow.invoice.couponUnsupported', def: "This promo code type isn't supported yet." };
    if (m.includes('coupon_exhausted'))
      return { key: 'avatarFlow.invoice.couponExhausted', def: 'This code has reached its limit.' };
    if (m.includes('coupon_expired'))
      return { key: 'avatarFlow.invoice.couponExpired', def: 'This code has expired.' };
  }
  return { key: 'avatarFlow.invoice.couponError', def: 'Code is invalid or already used.' };
}

export function PaymentSheet({
  skuCode,
  title,
  subtitle,
  priceStars,
  bullets,
  balanceHtml,
  thumbUrl,
  glyph,
  background,
  ctaLabel,
  isPaying = false,
  onPay,
  onApplyCoupon,
  onCancel,
  testId,
  payTestId,
  cancelTestId,
}: PaymentSheetProps): JSX.Element {
  const { t } = useTranslation('flows');
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [errState, setErrState] = useState<{ key: string; def: string } | null>(null);

  const handleApply = async (): Promise<void> => {
    const code = couponCode.trim();
    if (!code) return;
    setCouponBusy(true);
    setErrState(null);
    try {
      await onApplyCoupon(code);
      // On success the parent transitions out of the sheet (it unmounts).
    } catch (err) {
      setErrState(couponError(err));
      setCouponBusy(false);
    }
  };

  const payLabel = ctaLabel ?? t('avatarFlow.invoice.pay', { stars: priceStars, defaultValue: `Pay ${priceStars} ⭐` });
  const primaryLabel = isPaying ? t('avatarFlow.invoice.paying', { defaultValue: 'Opening Stars…' }) : payLabel;

  const couponBlock = (
    <div className={styles.couponBlock} data-testid="invoice-coupon">
      {couponOpen ? (
        <>
          <div className={styles.couponRow}>
            <input
              type="text"
              className={styles.couponInput}
              value={couponCode}
              onChange={(e) => {
                setCouponCode(e.target.value);
                setErrState(null);
              }}
              placeholder={t('avatarFlow.invoice.couponPlaceholder', { defaultValue: 'Promo code' })}
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              disabled={couponBusy}
              data-testid="invoice-coupon-input"
            />
            <button
              type="button"
              className={styles.couponApplyBtn}
              onClick={() => void handleApply()}
              disabled={couponBusy || couponCode.trim().length === 0}
              data-testid="invoice-coupon-apply"
            >
              {couponBusy
                ? t('avatarFlow.invoice.couponApplying', { defaultValue: 'Applying…' })
                : t('avatarFlow.invoice.couponApply', { defaultValue: 'Apply' })}
            </button>
          </div>
          {errState ? (
            <div className={styles.couponError} data-testid="invoice-coupon-error">
              {t(errState.key, { defaultValue: errState.def })}
            </div>
          ) : null}
        </>
      ) : (
        <button
          type="button"
          className={styles.couponToggle}
          onClick={() => setCouponOpen(true)}
          data-testid="invoice-coupon-toggle"
        >
          {t('avatarFlow.invoice.couponToggle', { defaultValue: 'Have a promo code?' })}
        </button>
      )}
    </div>
  );

  return (
    <InvoiceSheetShell
      skuCode={skuCode}
      title={title}
      subtitle={subtitle}
      priceStars={priceStars}
      bullets={bullets}
      balanceHtml={balanceHtml}
      thumbUrl={thumbUrl}
      glyph={glyph}
      background={background}
      ctaLabel={primaryLabel}
      onPrimary={onPay}
      primaryDisabled={isPaying || couponBusy}
      onCancel={onCancel}
      testId={testId}
      payTestId={payTestId}
      cancelTestId={cancelTestId}
    >
      {couponBlock}
    </InvoiceSheetShell>
  );
}

export default PaymentSheet;
